import {
  ILearningResourceCategory as IContentfulTopic,
  IDemo as IContentfulDemo,
  IBook as IContentfulBook,
  ILearnDirectoryLink as IContentfulArticle,
} from 'marketing-site/@types/generated/contentful'
import {
  ITopic,
  IBook,
  IArticle,
  IDemo,
} from 'marketing-site/src/components/bespoke/resources/topic'
import { getCategoryByKey } from 'marketing-site/src/library/utils/constants/categories'
import { transformCustomerStoryGrid } from './components/ContentfulEditorialStoryGrid'
import { transformSeoMetadata } from 'marketing-site/src/transformers/SEOMetadata'
import { RESOURCES_SLUG } from 'marketing-site/src/library/utils/constants/slugs'

export function transformTopic({ fields }: IContentfulTopic): ITopic {
  return {
    ...fields,
    title: fields.name,
    url: getSlug(fields.slug, fields.viewAllUrl),
    eyebrow: fields.eyebrow && getCategoryByKey(fields.eyebrow),
    resources: fields.resources && fields.resources.map(transformResource),
    customerStoryGrid:
      fields.editorialStoryGrid && transformCustomerStoryGrid(fields.editorialStoryGrid),
    shareImage: fields.shareImage.fields.file.url,
    components: fields.components?.fields.components,
    seo: fields.seo && transformSeoMetadata(fields.seo),
  }
}

type Resource = IContentfulDemo | IContentfulBook | IContentfulArticle

const isDemo = (entry: Resource): entry is IContentfulDemo =>
  (entry as IContentfulDemo).sys.contentType.sys.id === 'demo'

const isArticle = (entry: Resource): entry is IContentfulArticle =>
  (entry as IContentfulArticle).sys.contentType.sys.id === 'learnDirectoryLink'

const transformResource = (resource: Resource) => {
  if (isDemo(resource)) {
    return transformDemo(resource)
  } else if (isArticle(resource)) {
    return transformArticle(resource)
  } else {
    return transformBook(resource)
  }
}

function transformDemo({ fields }: IContentfulDemo): IDemo {
  return {
    ...fields,
    url: `/resources/demos/${fields.slug}`,
    description: fields.shortDescription,
    type: 'demo',
    imageUrl: fields.shareImage
      ? fields.shareImage.fields.file.url
      : fields.thumbnail.fields.file.url,
  }
}

function transformBook({ fields }: IContentfulBook): IBook {
  return {
    ...fields,
    type: fields.resourceType,
    url: `/resources/books/${fields.slug}`,
    imageUrl: fields.shareImage
      ? fields.shareImage.fields.file.url
      : fields.coverPhoto.fields.image.fields.file.url,
    seo: fields.seo && transformSeoMetadata(fields.seo),
  }
}

function transformArticle({ fields }: IContentfulArticle): IArticle {
  return {
    ...fields,
    type: 'article',
    imageUrl: fields.coverPhoto.fields.image.fields.file.url,
  }
}

export function getSlug(slug: string, viewAllUrl?: string): string {
  return viewAllUrl ? `/${viewAllUrl}` : `/${RESOURCES_SLUG}/${slug}`
}
